import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Stack } from '@mui/material';

import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import ApprovalQueue from './ApprovalQueue';
import ChecklistToolbar from './ChecklistToolbar';
import ContextCard from './ContextCard';
import PlaceholderChecklistTab from './PlaceholderChecklistTab';
import { CM_CHECKLIST_CATEGORIES } from './constants';
import { CapacityReportCard, MarginReportCard, SetupReportCard, TaxesPaymentsReportCard } from './reportCards';
import type { SetupChecklistRow, SetupRuleInput } from './setupRules';
import { SETUP_EVIDENCE_LABELS, buildSetupChecklistRules } from './setupRules';
import type {
  ApprovalQueueItem,
  ApprovalReportSummary,
  ChecklistCategory,
  ChecklistCategoryId,
  ChecklistRow,
  ChecklistTabId,
  EditedCapacityValues,
  EditedMarginValues,
  ManualApprovalState,
  SetupInputOverrides,
} from './types';
import {
  ChecklistStatus,
  EgsCapacityChecklistRow,
  EgsMarginChecklistRow,
  TaxesPaymentsChecklistRow,
  buildEgsCapacityRows,
  buildEgsMarginRows,
  buildTaxesPaymentsRows,
  calculateMarginPercentage,
  formatMoney,
  formatOptionalNumber,
  getMarginBand,
  getMarginStatus,
  getStatusLabel,
  isFiniteNumber,
} from './utils';

const EGS_CATEGORY = getChecklistCategory('egs');
const SETUP_CATEGORY = getChecklistCategory('setup');
const TAXES_PAYMENTS_CATEGORY = getChecklistCategory('taxesPayments');

interface Props {
  bookByDate?: string;
  campaignType?: string;
  channelManager?: string;
  hotelName: string;
  isLoadingProperty: boolean;
  isLoadingSurcharges: boolean;
  offerId: string;
  opportunityContract?: App.Contract;
  opportunityError?: unknown;
  packages?: Array<App.Package>;
  propertyId?: string;
  propertyError?: unknown;
  propertyHasTaxesPayableAtProperty: boolean;
  propertyRoomTypes?: Array<App.RoomType>;
  relatedExtraGuestSurcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
  isLoadingSetup: boolean;
  setupInput: SetupRuleInput;
  surcharges: Array<SalesforceSurchargePeriod>;
  surchargesError?: unknown;
  travelToDate?: string;
  vendorId?: string;
}

export default function CMChecklistWorkspace({
  bookByDate,
  campaignType,
  channelManager,
  hotelName,
  isLoadingProperty,
  isLoadingSurcharges,
  offerId,
  opportunityContract,
  opportunityError,
  packages,
  propertyId,
  propertyError,
  propertyHasTaxesPayableAtProperty,
  propertyRoomTypes,
  relatedExtraGuestSurcharges,
  isLoadingSetup,
  setupInput,
  surcharges,
  surchargesError,
  travelToDate,
  vendorId,
}: Props) {
  const [showAutoApproved, setShowAutoApproved] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTabId, setActiveTabId] = useState<ChecklistTabId>('approvalQueue');
  const [expandedQueueItems, setExpandedQueueItems] = useState<Record<string, boolean>>({});
  const [setupInputOverrides, setSetupInputOverrides] = useState<SetupInputOverrides>({});
  const [debouncedSetupInputOverrides, setDebouncedSetupInputOverrides] = useState<SetupInputOverrides>({});
  const [editedMargins, setEditedMargins] = useState<EditedMarginValues>({});
  const [editedCapacities, setEditedCapacities] = useState<EditedCapacityValues>({});
  const [manualApprovals, setManualApprovals] = useState<ManualApprovalState>({});

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setDebouncedSetupInputOverrides(setupInputOverrides);
    }, 400);

    return () => window.clearTimeout(debounceTimer);
  }, [setupInputOverrides]);

  const setupRows = useMemo(
    () => buildSetupChecklistRules(applySetupInputOverrides(setupInput, debouncedSetupInputOverrides)),
    [debouncedSetupInputOverrides, setupInput],
  );
  const baseMarginRows = useMemo(
    () => buildEgsMarginRows(surcharges, relatedExtraGuestSurcharges),
    [relatedExtraGuestSurcharges, surcharges],
  );
  const baseCapacityRows = useMemo(
    () => buildEgsCapacityRows({ packages, propertyRoomTypes, surcharges }),
    [packages, propertyRoomTypes, surcharges],
  );
  const propertyDataAvailable = propertyRoomTypes !== undefined;
  const taxesPaymentsRows = useMemo(
    () =>
      buildTaxesPaymentsRows({
        cityOtherTax: opportunityContract?.city_other_tax__c,
        cityTaxComments: opportunityContract?.city_tax_comments__c,
        hasPropertyTaxesPayableAtProperty: propertyHasTaxesPayableAtProperty,
        opportunityDataAvailable: !!opportunityContract,
        paymentMethod: opportunityContract?.payment_method__c,
        paymentMilestone: opportunityContract?.payment_milestone__c,
        prizeOrderId: opportunityContract?.prize_order_id__c,
        propertyDataAvailable,
      }),
    [opportunityContract, propertyHasTaxesPayableAtProperty, propertyDataAvailable],
  );

  const marginRows = useMemo(
    () => applyEditedMarginRows(baseMarginRows, editedMargins),
    [baseMarginRows, editedMargins],
  );
  const capacityRows = useMemo(
    () => applyEditedCapacityRows(baseCapacityRows, editedCapacities),
    [baseCapacityRows, editedCapacities],
  );
  const marginRowsById = useMemo(() => new Map(marginRows.map((row) => [row.id, row])), [marginRows]);
  const capacityRowsById = useMemo(() => new Map(capacityRows.map((row) => [row.id, row])), [capacityRows]);

  useEffect(() => {
    setSetupInputOverrides({});
    setDebouncedSetupInputOverrides({});
    setEditedMargins({});
    setEditedCapacities({});
    setManualApprovals({});
    setExpandedQueueItems({});
  }, [packages, propertyRoomTypes, relatedExtraGuestSurcharges, setupInput, surcharges]);

  const isApprovalQueueMode = activeTabId === 'approvalQueue';
  const activeCategory = isApprovalQueueMode ? SETUP_CATEGORY : getChecklistCategory(activeTabId);
  const isSetupCategoryActive = activeCategory.id === 'setup';
  const isEgsCategoryActive = activeCategory.id === 'egs';
  const isTaxesPaymentsCategoryActive = activeCategory.id === 'taxesPayments';
  const visibleSetupRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isSetupCategoryActive || isApprovalQueueMode ? setupRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getSetupSearchText,
      }),
    [isApprovalQueueMode, isSetupCategoryActive, manualApprovals, searchQuery, setupRows, showAutoApproved],
  );
  const visibleMarginRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isEgsCategoryActive || isApprovalQueueMode ? marginRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getMarginSearchText,
      }),
    [isApprovalQueueMode, isEgsCategoryActive, manualApprovals, marginRows, searchQuery, showAutoApproved],
  );
  const visibleCapacityRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isEgsCategoryActive || isApprovalQueueMode ? capacityRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getCapacitySearchText,
      }),
    [capacityRows, isApprovalQueueMode, isEgsCategoryActive, manualApprovals, searchQuery, showAutoApproved],
  );
  const visibleTaxesPaymentsRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isTaxesPaymentsCategoryActive || isApprovalQueueMode ? taxesPaymentsRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getTaxesPaymentsSearchText,
      }),
    [
      taxesPaymentsRows,
      isApprovalQueueMode,
      isTaxesPaymentsCategoryActive,
      manualApprovals,
      searchQuery,
      showAutoApproved,
    ],
  );
  const visibleQueueItems = useMemo(
    () => [
      ...visibleSetupRows.map((row) => buildSetupQueueItem(row)),
      ...visibleMarginRows.map((row) => buildMarginQueueItem(row)),
      ...visibleCapacityRows.map((row) => buildCapacityQueueItem(row)),
      ...visibleTaxesPaymentsRows.map((row) => buildTaxesPaymentsQueueItem(row)),
    ],
    [visibleCapacityRows, visibleMarginRows, visibleSetupRows, visibleTaxesPaymentsRows],
  );
  const reportSummaries = useMemo(
    () =>
      buildReportSummaries({
        taxesPaymentsRows,
        capacityRows,
        manualApprovals,
        marginRows,
        propertyId,
        setupRows,
        vendorId,
      }),
    [taxesPaymentsRows, capacityRows, manualApprovals, marginRows, propertyId, setupRows, vendorId],
  );
  const areVisibleQueueItemsExpanded =
    visibleQueueItems.length > 0 && visibleQueueItems.every((queueItem) => expandedQueueItems[queueItem.id]);
  const summary = useChecklistSummary({ taxesPaymentsRows, capacityRows, manualApprovals, marginRows, setupRows });
  const isLoading = isLoadingProperty || isLoadingSetup || isLoadingSurcharges;
  const hasError = !!propertyError || !!surchargesError;

  if (hasError) {
    return <Alert severity="error">Unable to load the CM checklist data.</Alert>;
  }

  if (isLoading) {
    return <Alert severity="info">Loading CM checklist data.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <ContextCard
        approvedCount={summary.approvedCount}
        bookByDate={bookByDate}
        campaignType={campaignType}
        channelManager={channelManager}
        hotelName={hotelName}
        needsApprovalCount={summary.needsApprovalCount}
        onSelectApprovalQueue={() => setActiveTabId('approvalQueue')}
        offerId={offerId}
        travelToDate={travelToDate}
        totalCount={summary.totalCount}
      />

      <ChecklistToolbar
        activeTabId={activeTabId}
        areVisibleQueueItemsExpanded={areVisibleQueueItemsExpanded}
        isApprovalQueueMode={isApprovalQueueMode}
        needsApprovalCount={summary.needsApprovalCount}
        searchQuery={searchQuery}
        showAutoApproved={showAutoApproved}
        visibleQueueItemCount={visibleQueueItems.length}
        onExpandVisibleCards={() =>
          setExpandedQueueItems((current) => toggleVisibleQueueItems(current, visibleQueueItems))
        }
        onSearchQueryChange={setSearchQuery}
        onShowAutoApprovedChange={() => setShowAutoApproved((current) => !current)}
        onTabChange={setActiveTabId}
      />

      {isApprovalQueueMode ? (
        <ApprovalQueue
          expandedQueueItems={expandedQueueItems}
          items={visibleQueueItems}
          needsApprovalCount={summary.needsApprovalCount}
          onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
          onToggleDetails={(itemId) => setExpandedQueueItems((current) => ({ ...current, [itemId]: !current[itemId] }))}
          reportSummaries={reportSummaries}
        />
      ) : isSetupCategoryActive ? (
        <SetupReportCard
          hiddenApprovedCount={setupRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          inputOverrides={setupInputOverrides}
          manualApprovals={manualApprovals}
          rows={visibleSetupRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
          onInputOverrideChange={(rowId, inputLabel, value) => {
            setSetupInputOverrides((current) => ({
              ...current,
              [rowId]: {
                ...current[rowId],
                [inputLabel]: value,
              },
            }));
            setManualApprovals((current) => {
              const { [rowId]: _removedApproval, ...nextApprovals } = current;
              return nextApprovals;
            });
          }}
        />
      ) : isEgsCategoryActive ? (
        <>
          <MarginReportCard
            hiddenApprovedCount={marginRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleMarginRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
            onValueChange={(rowId, fieldName, value) =>
              setEditedMargins((current) => ({
                ...current,
                [rowId]: {
                  costPrice: current[rowId]?.costPrice ?? marginRowsById.get(rowId)?.costPrice,
                  sellPrice: current[rowId]?.sellPrice ?? marginRowsById.get(rowId)?.sellPrice,
                  [fieldName]: value,
                },
              }))
            }
          />

          <CapacityReportCard
            hiddenApprovedCount={capacityRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleCapacityRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
            onValueChange={(rowId, fieldName, value) =>
              setEditedCapacities((current) => ({
                ...current,
                [rowId]: {
                  egsCapacity: current[rowId]?.egsCapacity ?? capacityRowsById.get(rowId)?.egsCapacity,
                  includedCapacity: current[rowId]?.includedCapacity ?? capacityRowsById.get(rowId)?.includedCapacity,
                  maxRoomCapacity: current[rowId]?.maxRoomCapacity ?? capacityRowsById.get(rowId)?.maxRoomCapacity,
                  [fieldName]: value,
                },
              }))
            }
          />
        </>
      ) : isTaxesPaymentsCategoryActive ? (
        <>
          {opportunityError && (
            <Alert severity="warning">
              Unable to load Salesforce opportunity data — some rules may show as missing data.
            </Alert>
          )}
          <TaxesPaymentsReportCard
            expandedRows={expandedQueueItems}
            hiddenApprovedCount={taxesPaymentsRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleTaxesPaymentsRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
            onToggleDetails={(itemId) =>
              setExpandedQueueItems((current) => ({ ...current, [itemId]: !current[itemId] }))
            }
            onUnapprovalChange={(rowId) => setManualApprovals(({ [rowId]: _, ...rest }) => rest)}
          />
        </>
      ) : (
        <PlaceholderChecklistTab category={activeCategory} />
      )}
    </Stack>
  );
}

function applyEditedMarginRows(rows: Array<EgsMarginChecklistRow>, editedMargins: EditedMarginValues) {
  return rows.map((row) => {
    const edited = editedMargins[row.id];
    if (!edited) {
      return row;
    }

    const costPrice = edited.costPrice;
    const sellPrice = edited.sellPrice;
    const margin = calculateMarginPercentage(costPrice, sellPrice);
    const marginBand = getMarginBand(margin);
    const status = getMarginStatus(marginBand);

    return {
      ...row,
      costPrice,
      margin,
      marginBand,
      sellPrice,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function applyEditedCapacityRows(rows: Array<EgsCapacityChecklistRow>, editedCapacities: EditedCapacityValues) {
  return rows.map((row) => {
    const edited = editedCapacities[row.id];
    if (!edited) {
      return row;
    }

    const { egsCapacity, includedCapacity, maxRoomCapacity } = edited;
    const hasAllValues =
      isFiniteNumber(egsCapacity) && isFiniteNumber(includedCapacity) && isFiniteNumber(maxRoomCapacity);
    const status: ChecklistStatus = hasAllValues
      ? maxRoomCapacity === includedCapacity + egsCapacity
        ? 'approved'
        : 'requires approval'
      : 'requires approval';

    return {
      ...row,
      egsCapacity,
      includedCapacity,
      maxRoomCapacity,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function applySetupInputOverrides(input: SetupRuleInput, inputOverrides: SetupInputOverrides): SetupRuleInput {
  const nextInput: SetupRuleInput = {
    ...input,
    account: { ...input.account },
    exclusions: input.exclusions ? { ...input.exclusions } : undefined,
    opportunity: { ...input.opportunity },
    property: { ...input.property },
  };

  Object.entries(inputOverrides).forEach(([rowId, values]) => {
    Object.entries(values).forEach(([label, value]) => {
      applySetupInputOverrideValue(nextInput, rowId, label, value);
    });
  });

  return nextInput;
}

function applySetupInputOverrideValue(input: SetupRuleInput, rowId: string, label: string, value: string) {
  switch (`${rowId}:${label}`) {
    case `setup-account-name:${SETUP_EVIDENCE_LABELS.accountName}`:
      input.account = { ...input.account, name: value };
      break;
    case `setup-account-name:${SETUP_EVIDENCE_LABELS.propertyName}`:
      input.property = { ...input.property, name: value };
      break;
    case `setup-account-name:${SETUP_EVIDENCE_LABELS.propertyWebsite}`:
      input.property = { ...input.property, website: value };
      break;
    case `setup-account-abn:${SETUP_EVIDENCE_LABELS.propertyCountry}`:
      input.property = { ...input.property, country: value };
      break;
    case `setup-account-abn:${SETUP_EVIDENCE_LABELS.abnVerified}`:
      input.account = { ...input.account, abnVerified: value };
      break;
    case `setup-account-abn:${SETUP_EVIDENCE_LABELS.abnStatus}`:
      input.account = { ...input.account, abnStatus: value };
      break;
    case `setup-account-abn:${SETUP_EVIDENCE_LABELS.abn}`:
      input.account = { ...input.account, abn: value };
      break;
    case `setup-account-currency:${SETUP_EVIDENCE_LABELS.hotelAccountCurrency}`:
    case `setup-sell-cost-reporting-currency:${SETUP_EVIDENCE_LABELS.reportingCurrency}`:
      input.account = { ...input.account, bankCurrency: value };
      break;
    case `setup-account-currency:${SETUP_EVIDENCE_LABELS.opportunityCurrency}`:
    case `setup-sell-cost-reporting-currency:${SETUP_EVIDENCE_LABELS.sellCurrency}`:
      input.opportunity = { ...input.opportunity, currencyIsoCode: value };
      break;
    case `setup-campaign-schedules-exclusions:${SETUP_EVIDENCE_LABELS.excludedBrands}`:
      input.exclusions = { ...input.exclusions, brands: value };
      break;
    case `setup-campaign-schedules-exclusions:${SETUP_EVIDENCE_LABELS.excludedCountries}`:
      input.exclusions = { ...input.exclusions, countries: value };
      break;
    case `setup-marketing-terms:${SETUP_EVIDENCE_LABELS.canonicalPropertyChain}`:
      input.chainName = value;
      break;
    case `setup-marketing-terms:${SETUP_EVIDENCE_LABELS.contractTemplateId}`:
    case `setup-contracts-geo:${SETUP_EVIDENCE_LABELS.contractTemplateId}`:
      input.account = { ...input.account, contractTemplateId: value };
      break;
    case `setup-contracts-geo:${SETUP_EVIDENCE_LABELS.leContractsGeo}`:
      input.opportunity = { ...input.opportunity, geo: value };
      break;
    case `setup-sell-cost-reporting-currency:${SETUP_EVIDENCE_LABELS.costCurrency}`:
      input.opportunity = { ...input.opportunity, costPriceCurrency: value };
      break;
    default:
      break;
  }
}

function useChecklistSummary({
  taxesPaymentsRows,
  capacityRows,
  manualApprovals,
  marginRows,
  setupRows,
}: {
  taxesPaymentsRows: Array<TaxesPaymentsChecklistRow>;
  capacityRows: Array<EgsCapacityChecklistRow>;
  manualApprovals: ManualApprovalState;
  marginRows: Array<EgsMarginChecklistRow>;
  setupRows: Array<SetupChecklistRow>;
}) {
  return useMemo(() => {
    const allRows = [...setupRows, ...marginRows, ...capacityRows, ...taxesPaymentsRows];
    const approvedCount = allRows.filter((row) => isRowApproved(row, manualApprovals)).length;
    const needsApprovalCount = allRows.length - approvedCount;

    return {
      approvedCount,
      needsApprovalCount,
      totalCount: allRows.length,
    };
  }, [taxesPaymentsRows, capacityRows, manualApprovals, marginRows, setupRows]);
}

function filterRows<Row>({
  isApproved,
  rows,
  searchQuery,
  showAutoApproved,
  text,
}: {
  isApproved: (row: Row) => boolean;
  rows: Array<Row>;
  searchQuery: string;
  showAutoApproved: boolean;
  text: (row: Row) => string;
}) {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return rows.filter((row) => {
    if (!showAutoApproved && isApproved(row)) {
      return false;
    }

    return !normalizedSearchQuery || text(row).toLowerCase().includes(normalizedSearchQuery);
  });
}

function buildMarginQueueItem(row: EgsMarginChecklistRow): ApprovalQueueItem {
  return {
    categoryId: EGS_CATEGORY.id,
    categoryLabel: EGS_CATEGORY.label,
    description: `${row.name} has a ${row.marginBand ? `${row.marginBand} ` : ''}margin check and needs review.`,
    details: [
      { label: 'Opportunity', value: row.opportunityName || 'Opportunity not mapped' },
      { label: 'Extra guest type', value: row.extraGuestType || 'N/A' },
      { label: 'Age range', value: row.ageRange || 'N/A' },
      { label: 'Cost price', value: formatMoney(row.costPrice, row.currencyCode) },
      { label: 'Sell price', value: formatMoney(row.sellPrice, row.currencyCode) },
      { label: 'Margin', value: typeof row.margin === 'number' ? `${row.margin.toFixed(1)}%` : 'N/A' },
      { label: 'Bedding type', value: row.beddingType || 'N/A' },
      { label: 'Inclusions', value: row.inclusions || 'N/A' },
      { label: 'Collected by', value: row.collectedBy || 'N/A' },
    ],
    id: row.id,
    title: row.name,
  };
}

function buildCapacityQueueItem(row: EgsCapacityChecklistRow): ApprovalQueueItem {
  const includedPlusEgs =
    isFiniteNumber(row.includedCapacity) && isFiniteNumber(row.egsCapacity)
      ? String(row.includedCapacity + row.egsCapacity)
      : 'N/A';

  return {
    categoryId: EGS_CATEGORY.id,
    categoryLabel: EGS_CATEGORY.label,
    description: `${row.name} capacity requires approval.`,
    details: [
      { label: 'Room asset', value: row.roomName },
      { label: 'Included capacity', value: formatOptionalNumber(row.includedCapacity) },
      { label: 'Max room capacity', value: formatOptionalNumber(row.maxRoomCapacity) },
      { label: 'EGS contracted for', value: formatOptionalNumber(row.egsCapacity) },
      { label: 'Included + EGS', value: includedPlusEgs },
    ],
    id: row.id,
    title: row.name,
  };
}

function buildSetupQueueItem(row: SetupChecklistRow): ApprovalQueueItem {
  return {
    categoryId: SETUP_CATEGORY.id,
    categoryLabel: SETUP_CATEGORY.label,
    description: row.summary,
    details: row.evidence.map((evidence) => ({ label: evidence.label, value: evidence.value })),
    id: row.id,
    title: row.field,
  };
}

function buildReportSummaries({
  taxesPaymentsRows,
  capacityRows,
  manualApprovals,
  marginRows,
  propertyId,
  setupRows,
  vendorId,
}: {
  taxesPaymentsRows: Array<TaxesPaymentsChecklistRow>;
  capacityRows: Array<EgsCapacityChecklistRow>;
  manualApprovals: ManualApprovalState;
  marginRows: Array<EgsMarginChecklistRow>;
  propertyId?: string;
  setupRows: Array<SetupChecklistRow>;
  vendorId?: string;
}): Array<ApprovalReportSummary> {
  const summaries: Array<ApprovalReportSummary> = [];

  const setupRowsRequiringApproval = setupRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (setupRowsRequiringApproval.length > 0) {
    const setupRowCopy = `setup row${setupRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const setupReviewVerb = setupRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: SETUP_CATEGORY.id,
      categoryLabel: SETUP_CATEGORY.label,
      count: setupRowsRequiringApproval.length,
      description: `${setupRowsRequiringApproval.length} ${setupRowCopy} still ${setupReviewVerb} review. Verify setup evidence before approval.`,
      sourceLinks: [],
    });
  }

  const egsRowsRequiringApproval = [...marginRows, ...capacityRows].filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  if (egsRowsRequiringApproval.length > 0) {
    const egsRowCopy = `EGS row${egsRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const egsReviewVerb = egsRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: EGS_CATEGORY.id,
      categoryLabel: EGS_CATEGORY.label,
      count: egsRowsRequiringApproval.length,
      description: `${egsRowsRequiringApproval.length} ${egsRowCopy} still ${egsReviewVerb} review. Verify source data in AP room-rate capacity and surcharge setup.`,
      sourceLinks: buildEgsSourceLinks({ capacityRows, propertyId, vendorId }),
    });
  }

  const taxesRowsRequiringApproval = taxesPaymentsRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (taxesRowsRequiringApproval.length > 0) {
    const taxesRowCopy = `rule${taxesRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const taxesReviewVerb = taxesRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: TAXES_PAYMENTS_CATEGORY.id,
      categoryLabel: TAXES_PAYMENTS_CATEGORY.label,
      count: taxesRowsRequiringApproval.length,
      description: `${taxesRowsRequiringApproval.length} Taxes & Payments ${taxesRowCopy} still ${taxesReviewVerb} review. Verify source data in the Salesforce opportunity contract and Admin Portal property taxes.`,
      sourceLinks: [],
    });
  }

  return summaries;
}

function buildEgsSourceLinks({
  capacityRows,
  propertyId,
  vendorId,
}: {
  capacityRows: Array<EgsCapacityChecklistRow>;
  propertyId?: string;
  vendorId?: string;
}) {
  if (!propertyId || !vendorId) {
    return [];
  }

  const sourceRow = capacityRows.find((row) => row.roomTypeId && row.roomRateId);

  if (!sourceRow?.roomTypeId || !sourceRow.roomRateId) {
    return [];
  }

  return [
    {
      href: `/vendors/${vendorId}/properties/${propertyId}/room-types/${sourceRow.roomTypeId}/room-rates/${sourceRow.roomRateId}`,
      label: 'Verify source data',
    },
  ];
}

function getChecklistCategory(categoryId: ChecklistCategoryId): ChecklistCategory {
  return CM_CHECKLIST_CATEGORIES.find((category) => category.id === categoryId) ?? CM_CHECKLIST_CATEGORIES[0];
}

function toggleVisibleQueueItems(
  current: Record<string, boolean>,
  visibleQueueItems: Array<ApprovalQueueItem>,
): Record<string, boolean> {
  const shouldExpand = !visibleQueueItems.every((queueItem) => current[queueItem.id]);

  return visibleQueueItems.reduce(
    (next, queueItem) => ({
      ...next,
      [queueItem.id]: shouldExpand,
    }),
    current,
  );
}

function isRowApproved(row: ChecklistRow, manualApprovals: ManualApprovalState) {
  return row.status === 'approved' || !!manualApprovals[row.id];
}

function buildTaxesPaymentsQueueItem(row: TaxesPaymentsChecklistRow): ApprovalQueueItem {
  return {
    categoryId: TAXES_PAYMENTS_CATEGORY.id,
    categoryLabel: TAXES_PAYMENTS_CATEGORY.label,
    description: `${row.name} requires review.`,
    details: row.details,
    id: row.id,
    title: row.name,
  };
}

function getMarginSearchText(row: EgsMarginChecklistRow) {
  return [
    row.name,
    row.opportunityName,
    row.extraGuestType,
    row.ageRange,
    row.currencyCode,
    row.beddingType,
    row.inclusions,
    row.collectedBy,
    row.statusLabel,
  ]
    .filter(Boolean)
    .join(' ');
}

function getCapacitySearchText(row: EgsCapacityChecklistRow) {
  return [row.name, row.roomName, row.statusLabel].filter(Boolean).join(' ');
}

function getSetupSearchText(row: SetupChecklistRow) {
  return [row.field, row.rule, row.summary, row.statusLabel, ...row.evidence.map((evidence) => evidence.value)]
    .filter(Boolean)
    .join(' ');
}

function getTaxesPaymentsSearchText(row: TaxesPaymentsChecklistRow) {
  return [row.name, row.field, row.statusLabel].filter(Boolean).join(' ');
}
